import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { API_BASE } from '../config';
import { getToken } from '../auth';
import { useTheme } from '../context/ThemeContext';
import { useAlert } from '../hooks/useAlert';
import { SkeletonForm } from '../components/Skeleton';
import {
  PageHeader, SectionLabel, Chip, AmountChip, SummaryCard, SummaryRow, PayButton, PleaseWait,
} from '../components/Form';
import Modal from '../components/Modal';
import Button from '../components/Button';
import Icon from '../components/Icon';
import { IconButton } from '../components/UI';

const WALLET_AMOUNT_OPTIONS = [10, 20, 30, 40, 50, 100, 200, 500, 1000];

interface PaymentMethod { id: string; name: string; paymentTag: string | null; logoUrl: string | null; }
interface PaymentAccount { id: string; label: string; deepLink: string; }

export default function WalletTopUp() {
  const navigate = useNavigate();
  const { colors } = useTheme();
  const { alert } = useAlert();
  const [paymentMethods, setPaymentMethods] = useState<PaymentMethod[]>([]);
  const [selectedMethod, setSelectedMethod] = useState<PaymentMethod | null>(null);
  const [selectedAmount, setSelectedAmount] = useState<number | null>(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [showPleaseWait, setShowPleaseWait] = useState(false);
  const [showPayModal, setShowPayModal] = useState(false);
  const [payAccount, setPayAccount] = useState<PaymentAccount | null>(null);

  useEffect(() => {
    axios.get<PaymentMethod[]>(`${API_BASE}/payment-methods`)
      .then(res => {
        setPaymentMethods(res.data);
        if (res.data.length > 0) setSelectedMethod(res.data[0]);
      })
      .catch(() => alert('Error', 'Failed to load payment methods. Please try again.'))
      .finally(() => setLoading(false));
  }, [alert]);

  const handleTopUp = async () => {
    const token = await getToken();
    if (!token) return alert('Not Signed In', 'Please sign in to continue.');
    if (!selectedMethod) return alert('Missing Info', 'Please select a payment method.');
    if (!selectedAmount) return alert('Select Amount', 'Please select a wallet top-up amount.');
    setSubmitting(true);
    try {
      const res = await axios.get<PaymentAccount>(
        `${API_BASE}/payment-methods/${selectedMethod.id}/random-account`,
        { headers: { Authorization: `Bearer ${token}` } },
      );
      setPayAccount(res.data);
      setShowPayModal(true);
    } catch (err: any) {
      alert('No Payment Account', err?.response?.data?.message || 'This payment method has no active accounts set up yet.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleConfirmPay = async () => {
    if (!payAccount || !selectedMethod || !selectedAmount) return;
    const token = await getToken();
    if (!token) return;
    setShowPayModal(false);

    try {
      await axios.post(
        `${API_BASE}/orders/topup`,
        {
          paymentMethodId: selectedMethod.id,
          paymentAccountId: payAccount.id,
          amount: selectedAmount,
          target: 'wallet',
          paymentSource: 'method',
        },
        { headers: { Authorization: `Bearer ${token}` } },
      );
    } catch (err: any) {
      alert('Order Error', err?.response?.data?.message || 'Wallet top-up could not be recorded.');
      return;
    }

    try { window.open(payAccount.deepLink, '_blank', 'noopener'); } catch { /* ignore */ }

    setSelectedAmount(null);
    setPayAccount(null);
    setShowPleaseWait(true);
    setTimeout(() => {
      setShowPleaseWait(false);
      navigate('/profile?openHistory=1');
    }, 2500);
  };

  const canPay = !!selectedMethod && !!selectedAmount;

  return (
    <div style={{ minHeight: '100dvh', backgroundColor: colors.bg }}>
      <PageHeader
        icon="wallet"
        title="Wallet Top-Up"
        right={<IconButton icon="arrow-left" accessibilityLabel="Back" onPress={() => navigate(-1)} />}
      />

      {loading ? (
        <div style={{ padding: 20 }}><SkeletonForm fields={3} /></div>
      ) : (
        <div style={{ padding: 20, paddingBottom: 40 }}>
          <SectionLabel>1. Select Amount</SectionLabel>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10 }}>
            {WALLET_AMOUNT_OPTIONS.map(amt => (
              <AmountChip key={amt} amount={amt} active={selectedAmount === amt} onClick={() => setSelectedAmount(amt)} />
            ))}
          </div>

          <SectionLabel>2. Payment Method</SectionLabel>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
            {paymentMethods.map(m => (
              <Chip
                key={m.id}
                active={selectedMethod?.id === m.id}
                onClick={() => setSelectedMethod(m)}
                icon={
                  m.logoUrl ? (
                    <img src={m.logoUrl} alt="" style={{ width: 18, height: 18, borderRadius: 4, objectFit: 'contain' }} />
                  ) : (
                    <Icon name="credit-card" size={13} color={selectedMethod?.id === m.id ? '#020617' : colors.textSub} />
                  )
                }
                label={m.name}
              />
            ))}
          </div>

          {canPay && (
            <SummaryCard title="Wallet Top-Up Summary">
              <SummaryRow label="Payment Method" value={selectedMethod?.name || ''} />
              <SummaryRow label="Amount" value={`$${selectedAmount}`} />
              <SummaryRow label="Wallet Credit" value="After admin verification" />
            </SummaryCard>
          )}

          <PayButton label="Top Up Wallet" onClick={handleTopUp} loading={submitting} disabled={!canPay} />
        </div>
      )}

      <Modal open={showPayModal} onClose={() => setShowPayModal(false)}>
        <div style={{ width: 36, height: 4, borderRadius: 2, backgroundColor: colors.divider, margin: '0 auto 16px' }} />
        <div style={{ fontSize: 17, fontWeight: 700, color: colors.text, marginBottom: 12 }}>Confirm Wallet Top-Up</div>
        <div style={{
          textAlign: 'center', padding: 20, borderRadius: 14,
          backgroundColor: colors.bg, border: `1px solid ${colors.cardBorder}`, marginBottom: 16,
        }}>
          <div style={{ fontSize: 36, fontWeight: 900, color: colors.text }}>${selectedAmount?.toFixed(2)}</div>
          <div style={{ fontSize: 14, color: colors.textSub, marginTop: 4 }}>via {selectedMethod?.name}</div>
          {payAccount && (
            <div style={{ display: 'inline-flex', alignItems: 'center', gap: 6, marginTop: 10 }}>
              <Icon name="link" size={12} color={colors.textSub} />
              <div style={{ fontSize: 13, fontWeight: 600, color: colors.textSub }}>{payAccount.label}</div>
            </div>
          )}
        </div>
        <div style={{
          display: 'flex', alignItems: 'flex-start', gap: 8,
          backgroundColor: colors.accentSoft, borderRadius: 12, padding: 12, marginBottom: 20,
        }}>
          <Icon name="info-circle" size={13} color={colors.accentText} style={{ marginTop: 2 }} />
          <div style={{ flex: 1, fontSize: 12, lineHeight: 1.5, color: colors.accentText }}>
            Your order is recorded before the payment app opens. The wallet balance updates after admin verification.
          </div>
        </div>
        <div style={{ display: 'flex', gap: 12 }}>
          <Button label="Cancel" variant="outline" onClick={() => setShowPayModal(false)} />
          <Button label="Open & Pay" icon="external-link-alt" onClick={handleConfirmPay} />
        </div>
      </Modal>

      <PleaseWait
        open={showPleaseWait}
        title="Payment Recorded"
        message="Wait a sec while your payment is being verified. Your wallet updates after admin approval."
      />
    </div>
  );
}
